import { useEffect, useState, useMemo } from "react";
import { api, API_BASE, apiHref } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Download, DollarSign, AlertCircle, RotateCcw } from "lucide-react";

const $ = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Statements() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [statement, setStatement] = useState(null);
  const [ageing, setAgeing] = useState(null);
  const [exportRange, setExportRange] = useState({ from: "", to: "" });
  const [includeExported, setIncludeExported] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);

  const loadExportStatus = async () => {
    try {
      const r = await api.get("/accounts/export/status");
      setExportStatus(r.data);
    } catch (_) {}
  };

  useEffect(() => { (async () => {
    const [cRes, aRes] = await Promise.all([
      api.get("/customers"),
      api.get("/accounts/ageing").catch(() => ({ data: null })),
    ]);
    setCustomers(cRes.data || []);
    setAgeing(aRes.data);
    loadExportStatus();
  })(); }, []);

  const [paying, setPaying] = useState(null);
  const togglePaid = async (inv) => {
    const paid = inv.status === "paid";
    if (!window.confirm(paid ? `Un-mark ${inv.invoice_no} as paid?` : `Mark ${inv.invoice_no} (${$(inv.total)}) as PAID?`)) return;
    setPaying(inv.id);
    try {
      await api.post(`/invoices/${inv.id}/${paid ? "unpay" : "pay"}`);
      toast.success(paid ? `${inv.invoice_no} back to unpaid` : `${inv.invoice_no} marked paid`);
      await loadStatement();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } finally { setPaying(null); }
  };
  const markAllPaid = async () => {
    const open = (statement?.invoices || []).filter((i) => !["paid", "voided", "cancelled"].includes(i.status));
    if (!open.length) return;
    const sum = open.reduce((a, i) => a + Number(i.total || 0), 0);
    if (!window.confirm(`Mark ${open.length} invoice(s) totalling ${$(sum)} as PAID?`)) return;
    setPaying("all");
    try {
      for (const i of open) await api.post(`/invoices/${i.id}/pay`);
      toast.success(`${open.length} invoice(s) marked paid`);
      await loadStatement();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } finally { setPaying(null); }
  };
  const loadStatement = async () => {
    if (!selectedCustomer) { toast.error("Pick a customer"); return; }
    const r = await api.get(`/customers/${selectedCustomer}/statement?month=${month}`);
    setStatement(r.data);
  };

  const downloadStatementPdf = () => {
    if (!selectedCustomer) return;
    window.open(apiHref(`/customers/${selectedCustomer}/statement/pdf?month=${month}`), "_blank");
  };

  const exportUrl = (fmt) => {
    const p = new URLSearchParams();
    if (exportRange.from) p.append("from", exportRange.from);
    if (exportRange.to) p.append("to", exportRange.to);
    if (includeExported) p.append("include_exported", "true");
    const qs = p.toString();
    return apiHref(`/accounts/export/${fmt}${qs ? `?${qs}` : ""}`);
  };

  const onExport = (fmt) => {
    // Give the download a moment to start, then refresh the pending counts
    window.open(exportUrl(fmt), "_blank");
    setTimeout(loadExportStatus, 1500);
  };

  const resetExport = async (destination) => {
    const label = destination === "all" ? "every destination" : destination.toUpperCase();
    if (!window.confirm(`Mark all invoices as UN-exported for ${label}? Next export will re-include everything.`)) return;
    try {
      const r = await api.post(`/accounts/export/reset?destination=${destination}`);
      toast.success(`Reset · ${r.data.cleared} invoices marked as unexported`);
      loadExportStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const totalsRow = ageing?.totals;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="statements-page">
      <div>
        <div className="label-tag">ACCOUNTS · STATEMENTS &amp; AGEING</div>
        <h1 className="heading text-3xl sm:text-4xl font-black uppercase tracking-tight">
          Statements <span className="text-primary-orange">·</span> <span className="text-primary-orange">Debtors</span>
        </h1>
      </div>

      {/* Statement generator */}
      <Card className="card-v2 p-5" data-testid="statement-generator">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-primary-orange" />
          <div className="label-tag">Monthly Statement</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="label-tag">Customer</Label>
            <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
              <SelectTrigger data-testid="statement-customer" className="rounded-lg bg-transparent border-industrial mono h-10"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
              <SelectContent className="rounded-lg bg-surface border-industrial max-h-72">
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="label-tag">Month</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-lg bg-transparent border-industrial mono h-10" data-testid="statement-month" /></div>
          <div className="flex gap-2">
            <Button data-testid="load-statement-btn" onClick={loadStatement} className="btn-cta flex-1">Load</Button>
            <Button data-testid="download-statement-pdf" onClick={downloadStatementPdf} variant="outline" className="rounded-lg border-industrial hover:bg-surface-2" title="Download PDF">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {statement && (
          <div className="mt-6 border-t border-industrial pt-4" data-testid="statement-preview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div><div className="label-tag">Opening</div><div className="mono text-lg font-bold text-ink">{$(statement.opening_balance)}</div></div>
              <div><div className="label-tag">Charges</div><div className="mono text-lg font-bold text-ink">{$(statement.charges)}</div></div>
              <div><div className="label-tag">Payments</div><div className="mono text-lg font-bold text-emerald-500">-{$(statement.payments)}</div></div>
              <div><div className="label-tag">Closing</div><div className={`mono text-lg font-bold ${statement.closing_balance > 0 ? "text-red-500" : "text-emerald-500"}`}>{$(statement.closing_balance)}</div></div>
            </div>
            {statement.invoices.length === 0 ? (
              <div className="text-sm text-muted-industrial">No invoices this month.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-industrial">
                  <th className="label-tag pb-2">Invoice</th><th className="label-tag pb-2">Issued</th><th className="label-tag pb-2">Due</th>
                  <th className="label-tag pb-2 text-right">Total</th><th className="label-tag pb-2">Status</th>
                  <th className="label-tag pb-2 text-right">
                    {statement.invoices.some((i) => !["paid", "voided", "cancelled"].includes(i.status)) && (
                      <button type="button" onClick={markAllPaid} disabled={!!paying} data-testid="statement-mark-all-paid"
                        className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-500/70 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50">
                        Mark all paid
                      </button>
                    )}
                  </th>
                </tr></thead>
                <tbody>
                  {statement.invoices.map((i) => (
                    <tr key={i.id} className="border-t border-industrial">
                      <td className="py-2 mono">{i.invoice_no}</td>
                      <td className="py-2 mono text-xs">{(i.issued_at || "").slice(0, 10)}</td>
                      <td className="py-2 mono text-xs">{(i.due_at || "").slice(0, 10)}</td>
                      <td className="py-2 text-right mono">{$(i.total)}</td>
                      <td className={`py-2 mono text-xs uppercase ${i.status === "paid" ? "text-emerald-400" : i.status === "voided" ? "text-muted-industrial" : "text-primary-orange"}`}>
                        {i.status}{i.status === "paid" && i.paid_at ? <span className="text-muted-industrial normal-case"> · {i.paid_at.slice(0, 10)}</span> : null}
                      </td>
                      <td className="py-2 text-right">
                        {!["voided", "cancelled"].includes(i.status) && (
                          <button type="button" onClick={() => togglePaid(i)} disabled={!!paying} data-testid={`statement-pay-${i.invoice_no}`}
                            className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border disabled:opacity-50 ${i.status === "paid"
                              ? "border-industrial text-muted-industrial hover:text-red-400 hover:border-red-500/60"
                              : "border-emerald-500/70 text-emerald-400 hover:bg-emerald-500/10"}`}>
                            {paying === i.id ? "…" : i.status === "paid" ? "Unpay" : "Mark paid"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>

      {/* Ageing */}
      <Card className="card-v2 p-5" data-testid="ageing-report">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-primary-orange" />
          <div className="label-tag">Debtor Ageing</div>
          {totalsRow && <span className="mono text-[10px] uppercase text-muted-industrial">Total outstanding {$(totalsRow.total)}</span>}
        </div>
        {!ageing || ageing.customers.length === 0 ? (
          <div className="text-sm text-muted-industrial">Nothing outstanding.</div>
        ) : (
          <table className="w-full text-sm" data-testid="ageing-table">
            <thead><tr className="text-left text-muted-industrial">
              <th className="label-tag pb-2">Customer</th>
              <th className="label-tag pb-2 text-right">Current</th>
              <th className="label-tag pb-2 text-right">30 d</th>
              <th className="label-tag pb-2 text-right">60 d</th>
              <th className="label-tag pb-2 text-right">90 d</th>
              <th className="label-tag pb-2 text-right">120 d+</th>
              <th className="label-tag pb-2 text-right">Total</th>
            </tr></thead>
            <tbody>
              {ageing.customers.map((r) => (
                <tr key={r.customer_id} className="border-t border-industrial hover:bg-surface-2">
                  <td className="py-2 font-semibold text-ink">{r.name}</td>
                  <td className="py-2 text-right mono">{$(r.current)}</td>
                  <td className="py-2 text-right mono text-amber-500">{$(r.d30)}</td>
                  <td className="py-2 text-right mono text-orange-500">{$(r.d60)}</td>
                  <td className="py-2 text-right mono text-red-500">{$(r.d90)}</td>
                  <td className="py-2 text-right mono text-red-600 font-bold">{$(r.d120)}</td>
                  <td className="py-2 text-right mono font-bold">{$(r.total)}</td>
                </tr>
              ))}
              {totalsRow && (
                <tr className="border-t-2 border-primary-orange">
                  <td className="py-2 font-black uppercase">TOTAL</td>
                  <td className="py-2 text-right mono">{$(totalsRow.current)}</td>
                  <td className="py-2 text-right mono">{$(totalsRow.d30)}</td>
                  <td className="py-2 text-right mono">{$(totalsRow.d60)}</td>
                  <td className="py-2 text-right mono">{$(totalsRow.d90)}</td>
                  <td className="py-2 text-right mono">{$(totalsRow.d120)}</td>
                  <td className="py-2 text-right mono font-black text-primary-orange">{$(totalsRow.total)}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>

      {/* Accounting export */}
      <Card className="card-v2 p-5" data-testid="accounting-export">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-primary-orange" />
          <div className="label-tag">Accounting Export</div>
        </div>
        <p className="text-xs text-muted-industrial mb-4">
          Sorted newest → oldest. By default only <b className="text-ink">new</b> invoices (never exported to this destination) are included, so you don't re-import the same rows twice.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div><Label className="label-tag">From</Label><Input type="date" value={exportRange.from} onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" data-testid="export-from" /></div>
          <div><Label className="label-tag">To</Label><Input type="date" value={exportRange.to} onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })} className="rounded-lg bg-transparent border-industrial mono h-10" data-testid="export-to" /></div>
          <label className="flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-muted-industrial cursor-pointer md:col-span-2" data-testid="export-include-exported-label">
            <input
              type="checkbox"
              data-testid="export-include-exported"
              checked={includeExported}
              onChange={(e) => setIncludeExported(e.target.checked)}
              className="accent-primary-orange w-4 h-4"
            />
            Include already-exported invoices (re-export everything in range)
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {[
            { key: "csv",  label: "Generic CSV" },
            { key: "xero", label: "Xero CSV" },
            { key: "myob", label: "MYOB CSV" },
          ].map((d) => {
            const pending = exportStatus?.unexported?.[d.key] ?? null;
            return (
              <div key={d.key} className="border border-industrial p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">{d.label}</div>
                  {pending !== null && (
                    <span
                      data-testid={`export-pending-${d.key}`}
                      className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${pending > 0 ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}
                    >
                      {pending} new
                    </span>
                  )}
                </div>
                <button
                  data-testid={`export-${d.key}`}
                  onClick={() => onExport(d.key)}
                  className="btn-cta inline-flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" /> Download {d.label}
                </button>
                <button
                  data-testid={`export-reset-${d.key}`}
                  onClick={() => resetExport(d.key)}
                  className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1 justify-center"
                  title={`Mark all invoices as UN-exported for ${d.label}`}
                >
                  <RotateCcw className="w-3 h-3" /> Reset
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-industrial mt-4">
          Xero: import via <span className="mono">Business → Invoices → Import → Sales Invoices</span>.
          MYOB: import via <span className="mono">File → Import Data → Sales Invoices</span> — the first column (<span className="mono">Card ID</span>) matches your customer card using the code you set on each Customer.
        </p>
      </Card>
    </div>
  );
}
